/**
 * Todo 业务逻辑纯函数：与 Context/Jotai 解耦，供 Todo 与 Ticket 共用，可单测。
 */
import { uniqBy } from 'lodash-es';
import type { TypeSource } from './NewTodo';
import { getDefaultTodoListMap } from './state';
import type { TodoListMap, RelatedInfo } from './state';

// ---------- TypeSource 构建 ----------

/**
 * 从 policies 构建 TypeSource（与 getTypeLabel 解耦，由调用方注入）
 * getTypeLabel 可返回 string | number，内部会转为 string
 */
export function buildTypeSource(
  policies: Todo.Policy[] | undefined,
  getTypeLabel: (type: Todo.Type) => string | number,
): TypeSource {
  if (!policies?.length) return [];
  return policies.map((p) => ({
    type: p.type,
    origin: p,
    typeLabel: String(p.type < 14 ? getTypeLabel(p.type) : p.name),
    results: uniqBy(p.templates, 'resultType').map((t) => t.resultType),
    subTypes: p.subTypes,
    subTypeTrees: p.subTypeTrees,
    enableManualCreate: p.enableManualCreate,
    enable: p.enable,
  })) as TypeSource;
}

// ---------- 关闭前校验 ----------

export type CloseTodoValidation = {
  valid: boolean;
  errorField?: string;
  /** i18n key for setFieldError / message */
  errorMessageKey?: string;
  /** params for t(errorMessageKey, errorMessageParams); nested keys like x 需由调用方再 t(param) */
  errorMessageParams?: Record<string, string>;
};

/**
 * 关闭待办前的业务校验（纯函数）。调用方根据 valid 决定是否调 closeTodo，并根据 errorField/errorMessageKey 展示错误。
 */
export function getCloseTodoValidation(todo: Todo.TodoInstance): CloseTodoValidation {
  const { operateResult, relatedReturnId } = todo;
  if (operateResult === 'RETURN_GOODS_REFUND' && !relatedReturnId) {
    return {
      valid: false,
      errorField: 'relatedReturnId',
      errorMessageKey: 'common.validation.requiredSelection',
      errorMessageParams: { x: 'domain.task.field.relatedReturn' },
    };
  }
  return { valid: true };
}

// ---------- 列表按状态分组与数量 ----------

/**
 * 待办实例列表按 todoStatus 分组为 TodoListMap
 */
export function list2Map(list: Todo.TodoInstance[]): TodoListMap {
  return list.reduce((acc, cur) => {
    acc[cur.todoStatus].push(cur);
    return acc;
  }, getDefaultTodoListMap());
}

/**
 * 从 TodoListMap 得到各状态数量
 */
export function getStatusMap(statusTodoMap: TodoListMap): Record<Todo.StatusKey, number> {
  return {
    1: statusTodoMap[1]?.length ?? 0,
    2: statusTodoMap[2]?.length ?? 0,
    3: statusTodoMap[3]?.length ?? 0,
    4: statusTodoMap[4]?.length ?? 0,
  };
}

// ---------- 当前 status 推导 ----------

/**
 * 根据 relatedMsgIdInfo、locateTodoId、statusCountMap 推导应选中的 todoStatus。
 * 若 locateTodoId 在 relatedMsgIdInfo 中则用其 status；否则按 1→4→2→1 优先级。
 */
export function deriveTodoStatusFromRelated(
  relatedMsgIdInfoList: RelatedInfo[],
  locateTodoIdValue: string | null,
  statusCountMap: Record<Todo.StatusKey, number>,
  currentStatus: Todo.StatusKey,
): Todo.StatusKey {
  const matchInfo = relatedMsgIdInfoList.find((item) => item.todoId === locateTodoIdValue);
  if (matchInfo) {
    return String(matchInfo.status) as Todo.StatusKey;
  }
  if (currentStatus === '3') {
    return '3';
  }
  if (statusCountMap[1] !== 0) return '1';
  if (statusCountMap[4] !== 0) return '4';
  if (statusCountMap[2] !== 0) return '2';
  return '1';
}

// ---------- 当前选中 id 推导 ----------

/**
 * 从当前列表与定位 id 推导应选中的 todo id：优先匹配 locateId，否则取列表第一项。
 */
export function deriveCurrentIdFromList(
  currentList: Todo.TodoInstance[],
  locateId: string | null,
): string | undefined {
  const match = currentList.find((item) => item.id === locateId);
  if (match) return match.id;
  return currentList?.[0]?.id;
}

// ---------- 从 list 提取 relatedMsgIdInfo（纯函数，供 useList onSuccess 用） ----------

/**
 * 从 TodoListMap 提取 relatedMsgIdInfo 列表（所有带 relatedMsgId 的待办）
 */
export function extractRelatedMsgIdInfo(statusTodoMap: TodoListMap): RelatedInfo[] {
  const list = (statusTodoMap[1] || [])
    .concat(statusTodoMap[2] || [])
    .concat(statusTodoMap[3] || [])
    .concat(statusTodoMap[4] || []);
  return list
    .filter((todo) => !!todo.relatedMsgId)
    .map((todo) => ({
      msgId: todo.relatedMsgId!,
      todoId: todo.id,
      status: todo.todoStatus,
    }));
}

// ---------- 状态 Tab 配置 ----------

export type TodoStatusTabItem = { label: string; value: Todo.StatusKey };

/**
 * 返回状态 Tab 配置（待认领/待处理/处理中/已完成）。t 由调用方注入，便于单测与多端一致。
 */
export function getTodoStatusTabs(t: (key: string) => string): TodoStatusTabItem[] {
  return [
    { label: t('scene.chatWorkspace.taskTab.status.pendingClaim'), value: '1' },
    { label: t('review.待处理'), value: '4' },
    { label: t('review.处理中'), value: '2' },
    { label: t('aide.已完成'), value: '3' },
  ];
}
